import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useLenis } from 'lenis/react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import WhatsAppButton from '../components/ui/WhatsAppButton';
import { PageTransition } from '../components/motion';
import Home from '../pages/Home';
import About from '../pages/About';
import Services from '../pages/Services';
import Tracking from '../pages/Tracking';
import Contact from '../pages/Contact';
import BookShipment from '../pages/BookShipment';
import ComingSoon from '../pages/ComingSoon';
import NotFound from '../pages/NotFound';
import Rate from '../pages/Rate';
import Kyc from '../pages/Kyc';
import Terms from '../pages/Legal/Terms';
import Privacy from '../pages/Legal/Privacy';

/** Routes with cross-fade page transitions + scroll reset on navigation. */
const AnimatedRoutes: React.FC = () => {
  const location = useLocation();
  const lenis = useLenis();

  // Start every newly-navigated page at the top.
  useEffect(() => {
    if (lenis) {
      // `force` so it still works if Lenis is paused (e.g. mid text-selection).
      lenis.scrollTo(0, { immediate: true, force: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, lenis]);

  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
        <Route
          path="/"
          element={
            <PageTransition>
              <Home />
            </PageTransition>
          }
        />
        <Route
          path="/about"
          element={
            <PageTransition>
              <About />
            </PageTransition>
          }
        />
        <Route
          path="/services"
          element={
            <PageTransition>
              <Services />
            </PageTransition>
          }
        />
        {/* Old domestic page (copied Oman text) — merged into Courier. */}
        <Route
          path="/services/domestic"
          element={<Navigate to="/services/courier" replace />}
        />
        <Route
          path="/services/:slug"
          element={
            <PageTransition>
              <Services />
            </PageTransition>
          }
        />
        <Route
          path="/tracking"
          element={
            <PageTransition>
              <Tracking />
            </PageTransition>
          }
        />
        <Route
          path="/book-shipment"
          element={
            <PageTransition>
              <BookShipment />
            </PageTransition>
          }
        />
        <Route
          path="/contact"
          element={
            <PageTransition>
              <Contact />
            </PageTransition>
          }
        />
        <Route
          path="/terms"
          element={
            <PageTransition>
              <Terms />
            </PageTransition>
          }
        />
        <Route
          path="/privacy"
          element={
            <PageTransition>
              <Privacy />
            </PageTransition>
          }
        />
        <Route
          path="/coming-soon"
          element={
            <PageTransition>
              <ComingSoon />
            </PageTransition>
          }
        />
        <Route
          path="*"
          element={
            <PageTransition>
              <NotFound />
            </PageTransition>
          }
        />
      </Routes>
    </AnimatePresence>
  );
};

const AppRoutes: React.FC = () => {
  const { pathname } = useLocation();
  // The rating link: just the rating, no site menu around it.
  // The KYC upload link: just the form, no site menu around it.
  if (pathname.startsWith('/kyc/')) {
    return (
      <Routes>
        <Route path="/kyc/:token" element={<Kyc />} />
      </Routes>
    );
  }
  if (pathname.startsWith('/rate/')) {
    return (
      <Routes>
        <Route path="/rate/:token" element={<Rate />} />
      </Routes>
    );
  }

  return (
    <>
      <Header />
      <main>
        <AnimatedRoutes />
      </main>
      <Footer />
      <WhatsAppButton />
    </>
  );
};

export default AppRoutes;
